<template>
	<CheckoutInfoBoxStatus
		:class="['offer-verified', blockClassMofifiers]"
		:status="status"
		:is-verified="offerData.availableChecked"
		bar-classes="offer-verified__top-bar"
	>
		<div :class="['offer__container']">
			<Loading
				v-if="loading"
				size="small"
				class="offer__loading"
			/>
			<OfferVerifiedBody
				:class="['offer__body', { 'is-loading': loading }]"
				:offer-data="offerData"
				:client="client"
				:operator="operator"
				:outbound-flight-segments="outboundFlightSegments"
				:inbound-flight-segments="inboundFlightSegments"
				:hotel-data="formattedHotelData"
				:alternative-flights-loading="alternativeFlightsLoading"
				:alternative-flights="alternativeFlights"
				:ibe-checkout-url="ibeCheckoutUrl"
				:is-verified="isVerified"
				@show-alternative-flights="openAlternativeFlightsModal"
				@verify-offer="emit('verify-offer')"
			/>
		</div>

		<AlternativeFlights
			ref="alternative-flight"
			:offer-data="offerData"
			:alternative-flights="alternativeFlights"
			:checkout-url="ibeCheckoutUrl"
		/>
	</CheckoutInfoBoxStatus>
</template>

<script lang="ts" setup>
import type { SearchFormDataType } from '@interfaces/search-form';
import { computed, onMounted, provide, ref, watchEffect } from 'vue';
import type { MergedOfferItemData, VerifiedFlightSegment, AlternativeFlightsDataArray } from '@interfaces/offer';
import type { ClientType, TourOperator } from '@/interfaces/common';
import { useStore } from '@/components/common/store';
import CheckoutInfoBoxStatus from '@lmt-rpb/CheckoutInfoBoxStatus/CheckoutInfoBoxStatus.vue';
import OfferVerifiedBody from './internals/OfferVerifiedBody.vue';
import AlternativeFlights from '@lmt-rpb/AlternativeFlights/AlternativeFlights.vue';
import { useBaseModal } from '@helper/composables/useBaseModal';
import type { OfferListTypoHotelData } from '@interfaces/offer-list-types/offer-list-typo-hotel-data';
import { getFlightSegments, getUnverifiedFlight, updateFlightSegments, generateCheckoutUrl } from './services/flightService';
import { fetchAlternativeFlights } from '@/components/common/services/alternativeFlightsService';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import { OFFER_VERIFIED_INJECTION_KEY, type OfferVerifiedInjectionProps } from './injectionProps';

interface Props {
	offerData: MergedOfferItemData;
	client?: ClientType;
	hotelData: OfferListTypoHotelData;
	isVerified?: boolean;
	autoDetails?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	client: 'at',
	isVerified: true,
	autoDetails: false,
});
const emit = defineEmits(['verify-offer']);

const store = useStore();
const [openAlternativeFlightsModal] = useBaseModal('alternative-flight');
const alternativeFlights = ref<AlternativeFlightsDataArray | false>(false);
const alternativeFlightsLoading = ref(false);
const isPackage = computed(() => props.offerData.type === 'package');

const formData = computed((): SearchFormDataType => store.state.searchMask);
const unavailable = computed(() => props.offerData.Status === 'NotAvailable' || (!props.offerData.available && isVerified.value));
const status = computed(() => (unavailable.value ? 'NotAvailable' : props.offerData.Status));

// Get flight segments data
const { first: firstOutboundFlightSegment, last: lastOutboundFlightSegment } = getFlightSegments(props.offerData.OutboundFlightSegments);
const { first: firstInboundFlightSegment, last: lastInboundFlightSegment } = getFlightSegments(props.offerData.InboundFlightSegments);
// Get unverified flight data
const unverifiedOutboundFlight = getUnverifiedFlight(props.offerData.OutboundFlight);
const unverifiedInboundFlight = getUnverifiedFlight(props.offerData.InboundFlight);
const loading = computed((): boolean => props.offerData.loading || false);
const isVerified = computed((): boolean => props.isVerified || false);

const blockClassMofifiers = computed(() => {
	return {
		'offer-verified--unverified': !isVerified.value,
		'offer-verified--unavailable': unavailable.value,
	};
});

provide<OfferVerifiedInjectionProps>(OFFER_VERIFIED_INJECTION_KEY, {
	isFetching: loading,
	isVerified,
	isNotAvailable: unavailable,
	isPackage,
});

// Convert hotelData to the format expected by OfferVerifiedBody
const formattedHotelData = computed(() => {
	return {
		Hotel: {
			IffCode: String(props.hotelData.Hotel.IffCode),
		},
	};
});

const ibeCheckoutUrl = computed((): string => {
	if (!props.isVerified || unavailable.value) return '';

	return generateCheckoutUrl(
		props.offerData,
		props.hotelData,
		{
			firstOutbound: firstOutboundFlightSegment,
			lastOutbound: lastOutboundFlightSegment,
			firstInbound: firstInboundFlightSegment,
			lastInbound: lastInboundFlightSegment,
		},
		{
			outbound: unverifiedOutboundFlight,
			inbound: unverifiedInboundFlight,
		}
	);
});

const operator = computed((): TourOperator => {
	const { Code, Name, Image } = props.offerData.TourOperator;
	const url = 'https://cluster2.images.traveltainment.eu/images/content/va_logos/cp/small';

	return {
		Name,
		Code,
		Image: Image || `${url}/${Code}.gif`,
	};
});
const inboundFlightSegments = computed((): VerifiedFlightSegment[] =>
	updateFlightSegments(props.offerData.InboundFlightSegments, unverifiedInboundFlight)
);

const outboundFlightSegments = computed((): VerifiedFlightSegment[] =>
	updateFlightSegments(props.offerData.OutboundFlightSegments, unverifiedOutboundFlight)
);

const getAlternativeFlights = async (): Promise<void> => {
	alternativeFlightsLoading.value = true;
	try {
		const result = await fetchAlternativeFlights(props.offerData, props.hotelData, formData.value);
		alternativeFlights.value = result;
	} finally {
		alternativeFlightsLoading.value = false;
	}
};

onMounted(() => {
	if (props.autoDetails) {
		emit('verify-offer');
	}
});

watchEffect(async () => {
	if (props.isVerified && isPackage.value && !unavailable.value) {
		await getAlternativeFlights();
	}
});
</script>

<style lang="scss" scoped>
.offer {
	&__loading {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate3d(-50%, -50%, 0);
		z-index: 1;
	}

	&__container {
		position: relative;
		width: 100%;
	}

	&__body {
		&.is-loading {
			opacity: 0.3;
		}
	}
}

.offer-verified {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	box-shadow: 0.25rem 0.4rem 1.4rem $color-black-t2;
	font-size: $font-small-1;
	font-weight: $font-weight-regular;
	border-radius: $border-radius-large;
	color: $color-text-light;

	&__header {
		margin-top: 2rem;
		width: 100%;
		padding: 0 1rem;
	}

	@media (min-width: $breakpoint-verysmall) {
		&__header {
			padding-left: 2rem;
			padding-right: 2rem;
		}
	}

	@media (min-width: $breakpoint-medium) {
		&__header {
			padding-left: 1rem;
			padding-right: 1rem;
		}
	}
}
</style>
